import { CompanyAvatar, CompanyAvatarEditor } from "./company-avatar";
import type { CompanyTree, Space } from "@rakazo/contracts";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from "@rakazo/ui-web";

import { ChevronDown, MoreHorizontal, Plus, Users } from "lucide-react";

import type { ReactNode } from "react";

import { useEffect, useState } from "react";

import type { createSpaceRpc } from "../../lib/rpc";

import { errorText } from "../../lib/user-error";

type Company = CompanyTree[number];

type Rpc = ReturnType<typeof createSpaceRpc>;

const selectClass =
  "h-11 w-full min-w-0 max-w-full rounded-lg border border-input bg-background px-3 text-base";

export function CompanyNavigation({
  rpc,
  spaces,
  currentSpaceId,
  currentBotId,
  openSpaceChat,
  refreshBots,
  renderRoster,
  onDeleteDepartment,
}: {
  rpc: Rpc;
  spaces: Space[];
  currentSpaceId?: string;
  currentBotId?: string;
  openSpaceChat: (id: string, path: string) => void;
  refreshBots: () => Promise<void>;
  renderRoster: (botIds: Set<string>, spaceIds: Set<string>) => ReactNode;
  onDeleteDepartment: (departmentId: string) => Promise<void>;
}) {
  const workingDepartment =
    currentSpaceId ?? spaces.find((s) => s.bots.some((b) => b.id === currentBotId))?.id;
  const [tree, setTree] = useState<CompanyTree>([]),
    [error, setError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<string | null>(null),
    [filter, setFilter] = useState<string>("all");

  const [manage, setManage] = useState<string | null>(null);

  const [membership, setMembership] = useState<{ companyId: string; botId: string } | null>(null);

  async function reload() {
    const next = await rpc.companies.list();
    setTree(next);
    setError(null);
  }

  useEffect(() => {
    let live = true;
    void rpc.companies
      .list()
      .then((next) => {
        if (live) {
          setTree(next);
          setError(null);
        }
      })
      .catch((e) => {
        if (live) setError(errorText(e, "Could not load companies"));
      });
    return () => {
      live = false;
    };
  }, [rpc, spaces]);

  useEffect(() => {
    const c = tree.find((c) => c.departments.some((d) => d.id === workingDepartment));
    if (c) setExpanded(c.id);
  }, [workingDepartment, tree.map((c) => c.id).join(",")]);

  async function saved() {
    await reload();
    await refreshBots();
  }

  function choose(c: Company, departmentId: string) {
    setExpanded(c.id);
    setFilter(departmentId);

    if (departmentId === "all") return;

    const logical = c.bots.find((b) => b.contexts.some((x) => x.botId === currentBotId));

    const context = logical?.contexts.find((x) => x.departmentId === departmentId && x.enabled);

    const first = spaces.find((s) => s.id === departmentId)?.bots[0];

    openSpaceChat(
      departmentId,
      context ? `/app/${context.botId}` : first ? `/app/${first.id}` : "/app",
    );
  }

  if (!tree.length)
    return error ? (
      <p role="alert" className="p-3 text-base text-destructive">
        {error}
        <Button variant="ghost" onClick={() => void reload()}>
          Retry
        </Button>
      </p>
    ) : (
      <div className="p-3 text-base text-muted-foreground">Loading companies…</div>
    );

  return (
    <div data-testid="company-tree" className="space-y-5 pt-3 text-base">
      {tree.map((c) => {
        const open = expanded === c.id;

        const departmentIds = new Set(
          c.departments
            .filter(
              (d) =>
                filter === "all" || d.id === filter || !c.departments.some((x) => x.id === filter),
            )
            .map((d) => d.id),
        );

        const allowed = new Set<string>();

        if (open) {
          for (const b of c.bots) {
            const candidates = b.contexts.filter(
              (x) => x.enabled && departmentIds.has(x.departmentId),
            );

            const context =
              candidates.find((x) => x.botId === currentBotId) ??
              candidates.find((x) => x.departmentId === workingDepartment) ??
              candidates[0];

            if (context) allowed.add(context.botId);
          }

          // Shared-bot sessions keep their existing private/channel authorization.

          const managed = new Set(c.bots.flatMap((b) => b.contexts.map((x) => x.botId)));

          for (const s of spaces.filter((s) => departmentIds.has(s.id)))
            for (const b of s.bots) if (!managed.has(b.id)) allowed.add(b.id);
        }

        return (
          <section key={c.id} aria-label={c.name}>
            <div className="flex min-h-11 items-center gap-1 px-1">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => {
                  setExpanded(open ? null : c.id);
                  setFilter("all");
                }}
                className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-lg px-2 text-start font-semibold hover:bg-accent"
              >
                <CompanyAvatar value={c.avatar} className="size-6 shrink-0" />
                <span className="truncate">{c.name}</span>
                <ChevronDown
                  className={`ms-auto size-4 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
                />
              </button>

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Manage ${c.name}`}
                onClick={() => setManage(c.id)}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </div>

            {open ? (
              <>
                <div className="mb-3 ms-4 space-y-1 border-s border-border ps-3">
                  <button
                    type="button"
                    aria-pressed={filter === "all"}
                    onClick={() => choose(c, "all")}
                    className={`min-h-11 w-full rounded-lg px-3 text-start ${filter === "all" ? "bg-accent font-medium" : "text-muted-foreground hover:bg-accent"}`}
                  >
                    All bots
                  </button>

                  {c.departments.map((d) => (
                    <button
                      type="button"
                      key={d.id}
                      aria-pressed={filter === d.id}
                      onClick={() => choose(c, d.id)}
                      className={`min-h-11 w-full rounded-lg px-3 text-start ${filter === d.id ? "bg-accent font-medium" : "text-muted-foreground hover:bg-accent"}`}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>

                {renderRoster(allowed, departmentIds)}

                {currentBotId &&
                c.bots.some((b) => b.contexts.some((x) => x.botId === currentBotId)) ? (
                  <Button
                    variant="ghost"
                    className="my-1 w-full justify-start gap-2 text-muted-foreground"
                    onClick={() =>
                      setMembership({
                        companyId: c.id,
                        botId: c.bots.find((b) => b.contexts.some((x) => x.botId === currentBotId))!
                          .id,
                      })
                    }
                  >
                    <Users className="size-4" />
                    Bot departments · {c.departments.find((d) => d.id === workingDepartment)?.name}
                  </Button>
                ) : null}

                {!allowed.size ? (
                  <p className="p-3 text-muted-foreground">No bots in this department.</p>
                ) : null}

                {c.canManage ? (
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-muted-foreground"
                    onClick={() => setManage(c.id)}
                  >
                    <Plus className="size-4" />
                    Add department
                  </Button>
                ) : null}
              </>
            ) : null}
          </section>
        );
      })}

      {manage && tree.find((c) => c.id === manage) ? (
        <CompanyManager
          company={tree.find((c) => c.id === manage)!}
          spaces={spaces}
          rpc={rpc}
          onClose={() => setManage(null)}
          onSave={saved}
          onDeleteDepartment={async (id) => {
            await onDeleteDepartment(id);
            setFilter((previous) => (previous === id ? "all" : previous));
            if (id === workingDepartment) setManage(null);
            else await reload();
          }}
          onMembership={(botId) => {
            setMembership({ companyId: manage, botId });
            setManage(null);
          }}
        />
      ) : null}

      {membership &&
      tree
        .find((c) => c.id === membership.companyId)
        ?.bots.find((b) => b.id === membership.botId) ? (
        <BotDepartments
          company={tree.find((c) => c.id === membership.companyId)!}
          botId={membership.botId}
          rpc={rpc}
          currentBotId={currentBotId}
          onClose={() => setMembership(null)}
          onSave={async (contexts) => {
            await saved();
            if (currentBotId && !contexts.some((x) => x.botId === currentBotId && x.enabled)) {
              const next = contexts.find((x) => x.enabled);
              if (next) openSpaceChat(next.departmentId, `/app/${next.botId}`);
            }
            setMembership(null);
          }}
        />
      ) : null}
    </div>
  );
}

function BotDepartments({
  company,
  botId,
  rpc,
  onClose,
  onSave,
  currentBotId,
}: {
  company: Company;
  botId: string;
  rpc: Rpc;
  onClose: () => void;
  onSave: (contexts: Company["bots"][number]["contexts"]) => Promise<void>;
  currentBotId?: string;
}) {
  const bot = company.bots.find((b) => b.id === botId)!;

  const initial = bot.contexts.filter((c) => c.enabled).map((c) => c.departmentId);

  const [ids, setIds] = useState(initial),
    [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null);

  const current = bot.contexts.find((c) => c.botId === currentBotId)?.departmentId ?? initial[0];

  const [moveTo, setMoveTo] = useState("");

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const result = await rpc.companies.memberships({
        companyId: company.id,
        botId,
        departmentIds: ids,
      });
      await onSave(result.contexts);
    } catch (e) {
      setError(errorText(e, "Could not update departments"));
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <DialogContent className="max-w-lg text-base">
        <DialogHeader>
          <DialogTitle>{bot.name} · Departments</DialogTitle>
          <DialogDescription>
            Each department keeps separate chats, memory, files and computer sessions. Removing
            membership stops that context and retains its history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {company.departments.map((d) => (
            <label
              key={d.id}
              className="flex min-h-11 items-center gap-3 rounded-lg px-2 hover:bg-accent"
            >
              <Checkbox
                checked={ids.includes(d.id)}
                onCheckedChange={(v) =>
                  setIds((old) =>
                    v ? [...old.filter((i) => i !== d.id), d.id] : old.filter((i) => i !== d.id),
                  )
                }
              />
              {d.name}
            </label>
          ))}
        </div>

        {current ? (
          <div className="space-y-2 border-t border-border pt-4">
            <label className="block">
              Move from {company.departments.find((d) => d.id === current)?.name}
              <select
                className={`${selectClass} mt-2`}
                value={moveTo}
                onChange={(e) => setMoveTo(e.target.value)}
              >
                <option value="">Choose department</option>
                {company.departments
                  .filter((d) => d.id !== current)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
              </select>
            </label>
            <Button
              variant="outline"
              disabled={!moveTo}
              onClick={() => {
                setIds((old) => [...new Set([...old.filter((id) => id !== current), moveTo])]);
                setMoveTo("");
              }}
            >
              Apply move
            </Button>
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save departments"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyManager({
  company: c,
  spaces,
  rpc,
  onClose,
  onSave,
  onMembership,
  onDeleteDepartment,
}: {
  company: Company;
  spaces: Space[];
  rpc: Rpc;
  onClose: () => void;
  onSave: () => Promise<void>;
  onMembership: (id: string) => void;
  onDeleteDepartment: (id: string) => Promise<void>;
}) {
  const [tab, setTab] = useState<"departments" | "members" | "resources">("departments"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(""),
    [companyName, setCompanyName] = useState(c.name);
  const [deleting, setDeleting] = useState<Company["departments"][number] | null>(null);

  const [department, setDepartment] = useState(""),
    [email, setEmail] = useState(""),
    [role, setRole] = useState<"member" | "admin">("member");

  const [resource, setResource] = useState(""),
    [grantScope, setGrantScope] = useState("company"),
    [target, setTarget] = useState(""),
    [allTools, setAllTools] = useState(false),
    [tools, setTools] = useState("");

  async function action(fn: () => Promise<unknown>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onSave();
    } catch (e) {
      setError(errorText(e, "Could not save changes"));
    } finally {
      setBusy(false);
    }
  }

  const resourceRow = c.resources.find((r) => `${r.kind}:${r.id}` === resource);
  async function deleteDepartment() {
    if (!deleting || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onDeleteDepartment(deleting.id);
      setDeleting(null);
    } catch (error) {
      setError(errorText(error, "Could not delete department"));
    } finally {
      setBusy(false);
    }
  }

  const people = department
    ? (c.departments.find((d) => d.id === department)?.members ?? [])
    : c.members;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] min-w-0 max-w-4xl grid-cols-1 overflow-y-auto text-base sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{c.name}</DialogTitle>
          <DialogDescription>Departments and access</DialogDescription>
        </DialogHeader>

        <div
          className="flex flex-wrap gap-2 border-b border-border pb-3"
          role="tablist"
          aria-label="Company management"
        >
          {(
            [
              ["departments", "Departments"],
              ["members", "People"],
              ["resources", "Connector access"],
            ] as const
          ).map(([id, label]) => (
            <Button
              key={id}
              role="tab"
              aria-selected={tab === id}
              variant={tab === id ? "secondary" : "ghost"}
              className="min-w-0 whitespace-normal"
              onClick={() => setTab(id)}
            >
              {label}
            </Button>
          ))}
        </div>

        {tab === "departments" ? (
          <div className="space-y-5">
            {c.canManage ? (
              <CompanyAvatarEditor
                value={c.avatar ?? null}
                disabled={busy}
                onError={setError}
                onSave={(avatar) =>
                  action(() => rpc.companies.setAvatar({ companyId: c.id, avatar }))
                }
              />
            ) : null}
            {c.canManage ? (
              <div className="flex gap-2">
                <Input
                  aria-label="Company name"
                  value={companyName}
                  maxLength={60}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
                <Button
                  variant="outline"
                  disabled={busy || !companyName.trim() || companyName === c.name}
                  onClick={() =>
                    void action(() => rpc.companies.rename({ companyId: c.id, name: companyName }))
                  }
                >
                  Rename
                </Button>
              </div>
            ) : null}

            {c.departments.map((d) => (
              <DepartmentRow
                key={d.id}
                companyId={c.id}
                department={d}
                rpc={rpc}
                action={action}
                busy={busy}
                onDelete={
                  spaces.some((s) => s.id === d.id && s.canDelete) && c.id !== `company:${d.id}`
                    ? () => {
                        setError(null);
                        setDeleting(d);
                      }
                    : undefined
                }
              />
            ))}

            {c.canManage ? (
              <div className="flex gap-2">
                <Input
                  aria-label="New department name"
                  placeholder="New department"
                  maxLength={60}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <Button
                  disabled={busy || !name.trim()}
                  onClick={() =>
                    void action(async () => {
                      await rpc.companies.createDepartment({ companyId: c.id, name });
                      setName("");
                    })
                  }
                >
                  Add
                </Button>
              </div>
            ) : null}

            <div className="space-y-2 border-t border-border pt-4">
              <h3 className="font-medium">Bots</h3>
              {c.bots.map((b) => (
                <div key={b.id} className="flex min-h-11 items-center justify-between gap-3">
                  <span className="min-w-0 truncate">{b.name}</span>
                  <Button variant="outline" disabled={busy} onClick={() => onMembership(b.id)}>
                    Departments
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {tab === "members" ? (
          <div className="space-y-4">
            <label className="block">
              Access to
              <select
                className={`${selectClass} mt-2`}
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              >
                {c.canManage ? (
                  <option value="">Entire company</option>
                ) : (
                  <option value="" disabled>
                    Choose department
                  </option>
                )}
                {c.departments
                  .filter((d) => d.canManage)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
              </select>
            </label>

            {people.map((m) => (
              <div
                key={m.userId}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium">{m.name}</p>
                  <p className="truncate text-muted-foreground">
                    {m.email} · {m.role}
                  </p>
                </div>
                {m.role !== "owner" ? (
                  <Button
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                      void action(() =>
                        rpc.companies.setMember({
                          companyId: c.id,
                          departmentId: department || undefined,
                          email: m.email,
                          role: null,
                        }),
                      )
                    }
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            ))}

            <Input
              type="email"
              aria-label="Teammate email"
              placeholder="Teammate email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <select
              aria-label="Access role"
              className={selectClass}
              value={role}
              onChange={(e) => setRole(e.target.value as typeof role)}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <Button
              disabled={busy || !email || (!c.canManage && !department)}
              onClick={() =>
                void action(() =>
                  rpc.companies.setMember({
                    companyId: c.id,
                    departmentId: department || undefined,
                    email,
                    role,
                  }),
                )
              }
            >
              Save access
            </Button>
          </div>
        ) : null}

        {tab === "resources" ? (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Choose where your connector accounts are available to your bots. Existing bot
              assignments and approval requirements remain in effect.
            </p>

            {c.grants.map((g) => (
              <div
                key={g.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0 flex-1 break-words">
                  <p className="font-medium">{g.name}</p>
                  <p className="text-muted-foreground">
                    {g.departmentId
                      ? c.departments.find((d) => d.id === g.departmentId)?.name
                      : g.botId
                        ? c.bots.find((b) => b.id === g.botId)?.name
                        : "Entire company"}{" "}
                    ·{" "}
                    {g.kind === "mcp"
                      ? g.allowAllTools
                        ? "All tools"
                        : `${g.allowedTools.length} tools`
                      : "Connected account"}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    void action(() =>
                      rpc.companies.setGrant({
                        companyId: c.id,
                        id: g.id,
                        kind: g.kind,
                        resourceId: g.resourceId,
                        remove: true,
                        allowAllTools: g.allowAllTools,
                        allowedTools: g.allowedTools,
                      }),
                    )
                  }
                >
                  Remove
                </Button>
              </div>
            ))}

            <label className="block">
              Account
              <select
                className={`${selectClass} mt-2`}
                value={resource}
                onChange={(e) => {
                  setResource(e.target.value);
                  const r = c.resources.find((r) => `${r.kind}:${r.id}` === e.target.value);
                  setAllTools(r?.allowAllTools ?? false);
                  setTools(r?.allowedTools.join(", ") ?? "");
                }}
              >
                <option value="">Choose account</option>
                {c.resources.map((r) => (
                  <option key={`${r.kind}:${r.id}`} value={`${r.kind}:${r.id}`}>
                    {r.name}
                  </option>
                ))}
              </select>
            </label>

            <select
              className={selectClass}
              aria-label="Grant scope"
              value={grantScope}
              onChange={(e) => {
                setGrantScope(e.target.value);
                setTarget("");
              }}
            >
              <option value="company">Entire company</option>
              <option value="department">One department</option>
              <option value="bot">One bot</option>
            </select>

            {grantScope !== "company" ? (
              <select
                className={selectClass}
                aria-label="Grant target"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">Choose {grantScope}</option>
                {(grantScope === "department" ? c.departments : c.bots).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            ) : null}

            {resourceRow?.kind === "mcp" ? (
              <>
                <label className="flex min-h-11 items-center gap-3">
                  <Checkbox checked={allTools} onCheckedChange={(v) => setAllTools(v === true)} />
                  Allow all tools
                </label>
                {!allTools ? (
                  <Input
                    aria-label="Allowed tool names"
                    placeholder="Allowed tool names, separated by commas"
                    value={tools}
                    onChange={(e) => setTools(e.target.value)}
                  />
                ) : null}
              </>
            ) : null}

            <Button
              disabled={busy || !resourceRow || (grantScope !== "company" && !target)}
              onClick={() =>
                void action(() =>
                  rpc.companies.setGrant({
                    companyId: c.id,
                    kind: resourceRow!.kind,
                    resourceId: resourceRow!.id,
                    departmentId: grantScope === "department" ? target : undefined,
                    botId: grantScope === "bot" ? target : undefined,
                    allowAllTools: allTools,
                    allowedTools: tools
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                    remove: false,
                  }),
                )
              }
            >
              Save connector access
            </Button>
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
        <AlertDialog
          open={deleting !== null}
          onOpenChange={(open) => {
            if (!open && !busy) setDeleting(null);
          }}
        >
          <AlertDialogContent className="text-base sm:max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="break-words">Delete {deleting?.name}?</AlertDialogTitle>
              <AlertDialogDescription className="text-base">
                This permanently deletes this department and its chats, files, memory, routines, and
                bot contexts. Bots in other departments stay there. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {error ? (
              <p role="alert" className="text-base text-destructive">
                {error}
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={busy}
                onClick={(event) => {
                  event.preventDefault();
                  void deleteDepartment();
                }}
              >
                {busy ? "Deleting…" : "Delete department"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

function DepartmentRow({
  companyId,
  department: d,
  rpc,
  action,
  busy,
  onDelete,
}: {
  companyId: string;
  department: Company["departments"][number];
  rpc: Rpc;
  action: (fn: () => Promise<unknown>) => Promise<void>;
  busy: boolean;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(d.name);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {d.canManage ? (
        <>
          <Input
            aria-label={`Department name ${d.name}`}
            value={name}
            maxLength={60}
            className="min-w-0 flex-1 basis-40"
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            variant="outline"
            disabled={busy || !name.trim() || name === d.name}
            onClick={() =>
              void action(() => rpc.companies.rename({ companyId, departmentId: d.id, name }))
            }
          >
            Rename
          </Button>
          {onDelete ? (
            <Button
              variant="destructive"
              disabled={busy}
              aria-label={`Delete ${d.name} department`}
              onClick={onDelete}
            >
              Delete
            </Button>
          ) : null}
        </>
      ) : (
        <span>{d.name}</span>
      )}
    </div>
  );
}
